import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { AuthHero } from "@/components/AuthHero";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Mail, Phone, Lock, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { ForgotPasswordModal } from "@/components/ForgotPasswordModal";

const Login = () => {
  const { login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const redirect = params.get("redirect") || "/dashboard";
  const [tab, setTab] = useState("email");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ email: "", mobile: "", password: "" });
  const [forgotOpen, setForgotOpen] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const identifier = tab === "email" ? form.email : form.mobile;
      const u = await login(identifier, form.password);
      toast.success("Welcome back!");
      // Admins land in the admin panel by default
      if (u?.role === "admin" && (redirect === "/dashboard" || !redirect)) {
        nav("/admin");
      } else {
        nav(redirect);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.05fr_1fr]">
      <AuthHero
        accent="Welcome back!"
        heading={<>Pick up where<br />you left off.</>}
        sub="Your saved rides, bookings and rider stats are waiting."
      />

      <div className="flex items-center justify-center px-6 py-12 bg-[#F8F9FA]">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden block mb-8"><Logo /></Link>

          <h1 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight">Log in to eBikeGo</h1>
          <p className="text-gray-600 mt-2">Use email or mobile to continue.</p>

          <Tabs value={tab} onValueChange={setTab} className="mt-7">
            <TabsList className="grid grid-cols-2 w-full bg-gray-100 rounded-full p-1 h-auto">
              <TabsTrigger value="email" data-testid="login-tab-email" className="rounded-full data-[state=active]:bg-white py-2.5">
                <Mail size={14} className="mr-2" />Email
              </TabsTrigger>
              <TabsTrigger value="mobile" data-testid="login-tab-mobile" className="rounded-full data-[state=active]:bg-white py-2.5">
                <Phone size={14} className="mr-2" />Mobile
              </TabsTrigger>
            </TabsList>

            <form onSubmit={submit} className="mt-6 space-y-4">
              <TabsContent value="email" className="space-y-2 m-0">
                <Label className="text-xs font-bold uppercase tracking-widest text-gray-500">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                  <Input data-testid="login-email-input" type="email" required={tab === "email"} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" className="pl-10 h-12 rounded-xl" />
                </div>
              </TabsContent>
              <TabsContent value="mobile" className="space-y-2 m-0">
                <Label className="text-xs font-bold uppercase tracking-widest text-gray-500">Mobile</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                  <Input data-testid="login-mobile-input" type="tel" required={tab === "mobile"} value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} placeholder="+91 98765 43210" className="pl-10 h-12 rounded-xl" />
                </div>
              </TabsContent>

              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-gray-500">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                  <Input data-testid="login-password-input" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••••" className="pl-10 h-12 rounded-xl" />
                </div>
                <div className="flex justify-end">
                  <button type="button" onClick={() => setForgotOpen(true)} data-testid="forgot-password-link" className="text-xs font-semibold text-gray-600 hover:text-[#2D7FF9]">
                    Forgot password?
                  </button>
                </div>
              </div>

              <button type="submit" disabled={loading} data-testid="login-submit-btn" className="btn-primary w-full justify-center !py-3.5 !text-base">
                {loading ? <Loader2 size={18} className="animate-spin" /> : <><span>Log in</span><ArrowRight size={16} /></>}
              </button>
            </form>
          </Tabs>

          <p className="text-center text-sm text-gray-600 mt-7">
            New to eBikeGo?{" "}
            <Link to="/signup" data-testid="goto-signup-link" className="font-bold text-[#0d8f5b] hover:text-[#2D7FF9]">Create an account</Link>
          </p>
        </div>
      </div>

      <ForgotPasswordModal open={forgotOpen} onClose={() => setForgotOpen(false)} />
    </div>
  );
};

export default Login;
